import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { RootState, StoreProps } from 'store';
import { addPermissions, WithPermissions } from '../../../decorators/addPermissions';
import { WithRouterProps } from '../../../decorators/withRouter';
import { Messages } from '../../../constants/rpcTypes';
import checkAccess from '../../../decorators/checkAccess';
import { FormField } from '../../../components/formFields/types';
import Loader from '../../../components/ui/loader';
import PageTemplate from 'components/pageTemplate/PageTemplate';
import FormFieldsContainer from '../../../components/formFields';
import { addTranslation, IntlProps } from '../../../decorators/addTranslation';
import { getFeatureById } from '../../../api/featureManage';
import { Feature } from '../types';
import {
  toggleFeatureToMerchant,
  toggleFeatureToProject,
  toggleFeatureToUser,
} from '../../../api/featureAssignment';
import { FilterTypes } from '../../../constants/FilterTypes';

interface ConnectedProps {
  user: any;
}

interface OwnProps {
  id: string;
  entityType: 'merchant' | 'project' | 'user';
}

type Props = OwnProps &
  IntlProps &
  ConnectedProps &
  StoreProps &
  WithPermissions &
  WithRouterProps;

interface State {
  featureData:  Feature | null;
  isLoading: boolean;
  isSubmitted: boolean;
  backendError: boolean;
  formValues: {
    merchantId?: string;
    projectId?: string;
    userId?: string;
  };
  validationErrors: { [key: string]: string };
}

@checkAccess([Messages.Feature_Get])
class FeatureAssignmentEntityContainer extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      featureData: null,
      isLoading: false,
      isSubmitted: false,
      backendError: false,
      formValues: {
        merchantId: undefined,
        projectId: undefined,
        userId: undefined
      },
      validationErrors: {}
    };
  }

  async componentDidMount() {
    await this.fetchFeatureData();
  }

  fetchFeatureData = async () => {
    const { match: { params: { id } }, getTranslate } = this.props;

    if (!id) return;

    try {
      this.setState({ loading: true, error: null });
      const response = await getFeatureById(id);

      this.setState({
        featureData: response,
        loading: false
      });
    } catch (err) {
      this.setState({
        error: getTranslate('features.errors.fetchFailed'),
        loading: false
      });
      console.error('Failed to fetch feature:', err);
    }
  };

  getFormConfig = (): { [key: string]: FormField[] } => {
    const { entityType } = this.props;
    const { getTranslate } = this.props;

    switch (entityType) {
      case 'merchant':
        return {
          general: [
            {
              id: 'merchantId',
              fieldType: FilterTypes.select,
              valueType: 'number',
              required: true,
              label: getTranslate('filters.types.merchantId'),
              placeholder: getTranslate('filters.types.merchantId'),
              dictionaryId: 'userMerchantWl',
              isSearchable: true,
              clearButton: true,
            }
          ]
        };

      case 'project':
        return {
          general: [
            {
              id: 'projectId',
              fieldType: FilterTypes.select,
              valueType: 'number',
              required: true,
              label: getTranslate('filters.fields.projectId.label'),
              placeholder: getTranslate('filters.fields.projectId.label'),
              dictionaryId: 'projectWl',
              isSearchable: true,
              clearButton: true,
            }
          ]
        };

      case 'user':
        return {
          general: [
            {
              id: 'userId',
              fieldType: FilterTypes.select,
              valueType: 'number',
              required: true,
              label: getTranslate('filters.types.userId'),
              placeholder: getTranslate('filters.types.userId'),
              dictionaryId: 'user',
              isSearchable: true,
              clearButton: true,
            }
          ]
        };

      default:
        return { general: [] };
    }
  };

  handleChange = (fieldId: string, value: any) => {
    const { validationErrors } = this.state;

    const newFormValues = { ...this.state.formValues, [fieldId]: value };

    const newValidationErrors = { ...validationErrors };
    delete newValidationErrors[fieldId];

    this.setState({
      formValues: newFormValues,
      validationErrors: newValidationErrors
    });
  };

  validateForm = (): boolean => {
    const { entityType } = this.props;
    const { formValues } = this.state;
    const { getTranslate } = this.props;
    const validationErrors: { [key: string]: string } = {};

    switch (entityType) {
      case 'merchant':
        if (!formValues.merchantId) {
          validationErrors.merchantId = getTranslate('common.requiredField.error');
        }
        break;
      case 'project':
        if (!formValues.projectId) {
          validationErrors.projectId = getTranslate('common.requiredField.error');
        }
        break;
      case 'user':
        if (!formValues.userId) {
          validationErrors.userId = getTranslate('common.requiredField.error');
        }
        break;
    }

    if (Object.keys(validationErrors).length > 0) {
      this.setState({ validationErrors });
      return false;
    }

    return true;
  };

  handleSubmit = async () => {
    const { match: { params: { id } },  entityType, getTranslate, history } = this.props;
    const { formValues } = this.state;

    if (!this.validateForm()) {
      return;
    }

    this.setState({ isLoading: true, backendError: false });

    const featureId = Number(id);

    try {
      switch (entityType) {
        case 'merchant':
          if (formValues.merchantId) {
            await toggleFeatureToMerchant(featureId, [formValues.merchantId].map(val => Number(val)));
          }
          break;
        case 'project':
          if (formValues.projectId) {
            await toggleFeatureToProject(featureId, [formValues.projectId].map(val => Number(val)));
          }
          break;
        case 'user':
          if (formValues.userId) {
            await toggleFeatureToUser(featureId, [formValues.userId].map(val => Number(val)));
          }
          break;
      }

      this.setState({
        isSubmitted: true,
        isLoading: false
      });

      history.push({
        pathname: this.getBackUrl(),
        search: `?tab=${entityType}s`,
      });


    } catch (error) {
      this.setState({
        backendError: true,
        isLoading: false,
        validationErrors: {
          _form: getTranslate('features.errors.assignmentFailed')
        }
      });
    }
  };

  handleReset = () => {
    this.setState({
      isSubmitted: false,
      formValues: {
        merchantId: undefined,
        projectId: undefined,
        userId: undefined
      },
      validationErrors: {}
    });
  };

  getBackUrl = () => {
    const { match: { params: { id } }, entityType } = this.props;

    return `/features/${id}?tab=${entityType}s`;
  };

  getFormTitle = () => {
    const { match: { params: { id } } } = this.props;
    const { featureData } = this.state;

    if (! featureData) {
      return `Feature :: ${id}`;
    }
    return `Feature :: ${id} ${featureData?.name}`;
  };

  render() {
    const {
      isLoading,
      isSubmitted,
      backendError,
      formValues,
      validationErrors,
      featureData
    } = this.state;

    const { getTranslate } = this.props;

    if (isLoading && !isSubmitted) {
      return (
        <PageTemplate.Main>
          <PageTemplate.Container>
            <Loader />
          </PageTemplate.Container>
        </PageTemplate.Main>
      );
    }

    const formConfig = this.getFormConfig();
    const canCreate = !isLoading && !isSubmitted;

    const enabledForAll =  getTranslate(featureData?.isEnabledForAll
      ? 'features.columns.isEnabledForAll.yesValue'
      : 'features.columns.isEnabledForAll.noValue')

    return (
      <PageTemplate.Main customClass={`feature-assignment-${this.props.entityType}-page`}>
        <PageTemplate.Container>
          <FormFieldsContainer
            subText={enabledForAll}
            id={`feature-assignment-${this.props.entityType}`}
            title={this.getFormTitle()}
            onReset={this.handleReset}
            isLoading={isLoading}
            isSubmitted={isSubmitted}
            backUrl={this.getBackUrl()}
            backText="common.back.button"
            createButtonText='common.save.button'
            fieldsConfig={formConfig}
            fieldsValues={formValues}
            validationErrors={validationErrors}
            onChange={this.handleChange}
            onSubmit={this.handleSubmit}
            canCreate={canCreate}
            status={backendError ? 'error' : undefined}
          />
        </PageTemplate.Container>
      </PageTemplate.Main>
    );
  }
}

const mapStateToProps = (state: RootState, props: OwnProps): ConnectedProps => ({
  user: state.user
});

export default withRouter(
  connect(mapStateToProps)(addTranslation(addPermissions(FeatureAssignmentEntityContainer)))
);